import { axiosRequest, type AxiosResult } from "@/lib/axios";
import type { FontsResponse } from "../types";

const API_BASE_URL = import.meta.env.VITE_SERVICE_CORE_URL;

// Client-side cache duration (1 hour in milliseconds)
const CACHE_DURATION = 60 * 60 * 1000;

interface CachedFontsData {
	data: FontsResponse;
	timestamp: number;
}

let cachedFonts: CachedFontsData | null = null;

export const fontApi = {
	/**
	 * Fetch fonts from the backend API with client-side caching
	 * The backend handles Google Fonts API calls and caching (24h)
	 * Client caches for 1 hour to reduce redundant calls
	 */
	getFonts: async (): Promise<AxiosResult<FontsResponse>> => {
		// Check client-side cache
		if (cachedFonts && Date.now() - cachedFonts.timestamp < CACHE_DURATION) {
			return {
				success: true,
				data: cachedFonts.data,
			};
		}

		const result = await axiosRequest<FontsResponse>({
			url: `${API_BASE_URL}/fonts`,
			method: "GET",
		});

		// Cache successful response
		if (result.success && result.data) {
			cachedFonts = {
				data: result.data,
				timestamp: Date.now(),
			};
		}

		return result;
	},

	/**
	 * Clear the client-side font cache
	 * Useful for testing or forcing a refresh
	 */
	clearCache: () => {
		cachedFonts = null;
	},
};
